import {
  collection,
  documentId,
  getDocs,
  limit,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase";

const CACHE_TTL_MS = 60 * 1000;
const STORAGE_PREFIX = "oss-shop-products-v2";
const CACHE_VERSION_KEY = `${STORAGE_PREFIX}:version`;

const sectionProductsCache = new Map();
const sectionProductsRequests = new Map();
const productBySlugCache = new Map();
const productBySlugRequests = new Map();
let allActiveProductsCache = null;
let allActiveProductsRequest = null;
const preloadedImages = new Set();
const PUBLIC_PRODUCTS_API_ORIGIN = "https://oss-shop-demo.web.app";

function storageAvailable() {
  return typeof window !== "undefined" && window.localStorage;
}

function isFresh(entry) {
  return entry && Date.now() - entry.cachedAt < CACHE_TTL_MS;
}

function getStorageKey(type, id) {
  return `${STORAGE_PREFIX}:${type}:${id}`;
}

function readCacheVersion() {
  if (!storageAvailable()) return "";

  return window.localStorage.getItem(CACHE_VERSION_KEY) || "";
}

function readStorageEntry(type, id) {
  if (!storageAvailable()) return null;

  try {
    const rawEntry = window.localStorage.getItem(getStorageKey(type, id));
    return rawEntry ? JSON.parse(rawEntry) : null;
  } catch (error) {
    return null;
  }
}

function writeStorageEntry(type, id, value) {
  if (!storageAvailable()) return;

  try {
    window.localStorage.setItem(
      getStorageKey(type, id),
      JSON.stringify({
        cachedAt: Date.now(),
        value
      })
    );
  } catch (error) {
    // If storage is full or blocked, the in-memory cache still keeps the app fast.
  }
}

function cacheSectionProducts(sectionId, products) {
  const entry = {
    cachedAt: Date.now(),
    value: products
  };

  sectionProductsCache.set(sectionId, entry);
  writeStorageEntry("section", sectionId, products);
}

function getCachedSectionProducts(sectionId) {
  const memoryEntry = sectionProductsCache.get(sectionId);

  if (isFresh(memoryEntry)) return memoryEntry.value;

  const storageEntry = readStorageEntry("section", sectionId);

  if (!isFresh(storageEntry)) return null;

  sectionProductsCache.set(sectionId, storageEntry);
  storageEntry.value.forEach(rememberProduct);

  return storageEntry.value;
}

function normalizeProduct(doc) {
  return {
    id: doc.id,
    ...doc.data()
  };
}

function getPublicProductsApiUrl(queryString) {
  const params = new URLSearchParams(queryString);
  const cacheVersion = readCacheVersion();

  if (cacheVersion) params.set("v", cacheVersion);

  const nextQueryString = params.toString();
  const apiPath = `/api/products${nextQueryString ? `?${nextQueryString}` : ""}`;

  if (typeof window === "undefined") return apiPath;

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  return isLocalhost ? `${PUBLIC_PRODUCTS_API_ORIGIN}${apiPath}` : apiPath;
}

async function fetchPublicProducts({ sectionId, slug, ids } = {}) {
  const params = new URLSearchParams();

  if (sectionId) params.set("sectionId", sectionId);
  if (slug) params.set("slug", slug);
  if (ids?.length) params.set("ids", ids.join(","));

  const queryString = params.toString();
  const response = await fetch(getPublicProductsApiUrl(queryString));

  if (!response.ok) {
    throw new Error("Public products API failed.");
  }

  return response.json();
}

function rememberProduct(product) {
  if (!product.slug) return;

  const entry = {
    cachedAt: Date.now(),
    value: product
  };

  productBySlugCache.set(product.slug, entry);
}

export function getCachedActiveProductBySlug(slug) {
  const memoryEntry = productBySlugCache.get(slug);

  if (isFresh(memoryEntry)) return memoryEntry.value;

  const storageEntry = readStorageEntry("slug", slug);

  if (!isFresh(storageEntry)) return null;

  productBySlugCache.set(slug, storageEntry);

  return storageEntry.value;
}

export function getProductImageUrl(product) {
  return product.images?.[0] || product.image || product.thumbnailUrl || "";
}

export function getProductImages(product) {
  if (product.images && product.images.length > 0) return product.images;
  return product.image ? [product.image] : [];
}

export function clearProductCaches() {
  sectionProductsCache.clear();
  productBySlugCache.clear();
  allActiveProductsCache = null;
  allActiveProductsRequest = null;

  if (!storageAvailable()) return;

  Object.keys(window.localStorage)
    .filter((key) => key.startsWith(`${STORAGE_PREFIX}:`))
    .forEach((key) => window.localStorage.removeItem(key));

  window.localStorage.setItem(CACHE_VERSION_KEY, String(Date.now()));
}

export function preloadProductImages(products, highPriorityCount = 4) {
  if (typeof window === "undefined") return;

  products.slice(0, highPriorityCount).forEach((product, index) => {
    const imageUrl = getProductImageUrl(product);

    if (!imageUrl || preloadedImages.has(imageUrl)) return;

    preloadedImages.add(imageUrl);

    const image = new Image();
    image.decoding = "async";
    image.fetchPriority = index < highPriorityCount ? "high" : "low";
    image.src = imageUrl;
  });
}

export async function getActiveProductsForSection(sectionId) {
  const cachedProducts = getCachedSectionProducts(sectionId);

  if (cachedProducts) {
    preloadProductImages(cachedProducts);
    return cachedProducts;
  }

  if (sectionProductsRequests.has(sectionId)) {
    return sectionProductsRequests.get(sectionId);
  }

  const request = fetchPublicProducts({ sectionId })
    .then(({ products }) => products || [])
    .catch(() =>
      Promise.all([
        getDocs(
          query(
            collection(db, "products"),
            where("sectionId", "==", sectionId),
            where("active", "==", true)
          )
        ),
        getDocs(
          query(
            collection(db, "products"),
            where("sectionIds", "array-contains", sectionId),
            where("active", "==", true)
          )
        )
      ]).then((snapshots) => {
      const productsById = new Map();

      snapshots.forEach((snapshot) => {
        snapshot.docs.forEach((productDoc) => {
          const product = normalizeProduct(productDoc);

          productsById.set(product.id, product);
        });
      });

        return [...productsById.values()];
      })
    )
    .then((products) => {

      products.forEach(rememberProduct);
      cacheSectionProducts(sectionId, products);
      preloadProductImages(products);

      return products;
    })
    .finally(() => {
      sectionProductsRequests.delete(sectionId);
    });

  sectionProductsRequests.set(sectionId, request);
  return request;
}

export async function getAllActiveProducts() {
  if (isFresh(allActiveProductsCache)) {
    return allActiveProductsCache.value;
  }

  const storageEntry = readStorageEntry("all", "active");

  if (isFresh(storageEntry)) {
    allActiveProductsCache = storageEntry;
    storageEntry.value.forEach(rememberProduct);
    return storageEntry.value;
  }

  if (allActiveProductsRequest) return allActiveProductsRequest;

  allActiveProductsRequest = fetchPublicProducts()
    .then(({ products }) => products || [])
    .catch(() =>
      getDocs(
        query(collection(db, "products"), where("active", "==", true))
      ).then((snapshot) => snapshot.docs.map(normalizeProduct))
    )
    .then((products) => {
      products.forEach(rememberProduct);
      allActiveProductsCache = {
        cachedAt: Date.now(),
        value: products
      };
      writeStorageEntry("all", "active", products);

      return products;
    })
    .finally(() => {
      allActiveProductsRequest = null;
    });

  return allActiveProductsRequest;
}

export async function getActiveProductBySlug(slug) {
  const cacheEntry = productBySlugCache.get(slug);

  if (isFresh(cacheEntry)) {
    preloadProductImages([cacheEntry.value], 1);
    return cacheEntry.value;
  }

  if (productBySlugRequests.has(slug)) {
    return productBySlugRequests.get(slug);
  }

  const request = fetchPublicProducts({ slug })
    .then(({ product }) => product || null)
    .catch(() =>
      getDocs(
        query(
          collection(db, "products"),
          where("slug", "==", slug),
          where("active", "==", true),
          limit(1)
        )
      ).then((snapshot) => {
      if (snapshot.empty) return null;

        return normalizeProduct(snapshot.docs[0]);
      })
    )
    .then((product) => {
      if (!product) return null;

      rememberProduct(product);
      writeStorageEntry("slug", product.slug, product);
      preloadProductImages([product], 1);

      return product;
    })
    .finally(() => {
      productBySlugRequests.delete(slug);
    });

  productBySlugRequests.set(slug, request);
  return request;
}

export async function getActiveProductsByIds(ids) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];

  if (uniqueIds.length === 0) return [];

  const cachedProducts = uniqueIds
    .map((id) => {
      for (const entry of productBySlugCache.values()) {
        if (isFresh(entry) && entry.value.id === id) return entry.value;
      }

      return null;
    })
    .filter(Boolean);
  const cachedIds = new Set(cachedProducts.map((product) => product.id));
  const missingIds = uniqueIds.filter((id) => !cachedIds.has(id));

  if (missingIds.length === 0) return cachedProducts;

  const loadedProducts = await fetchPublicProducts({ ids: missingIds })
    .then(({ products }) => products || [])
    .catch(async () => {
      const snapshots = await Promise.all(
        Array.from({ length: Math.ceil(missingIds.length / 10) }, (_, index) => {
          const chunk = missingIds.slice(index * 10, index * 10 + 10);

          return getDocs(
            query(
              collection(db, "products"),
              where(documentId(), "in", chunk)
            )
          );
        })
      );

      return snapshots.flatMap((snapshot) =>
        snapshot.docs.map(normalizeProduct)
      ).filter((product) => product.active);
    });

  loadedProducts.forEach(rememberProduct);

  const productsById = new Map(
    [...cachedProducts, ...loadedProducts].map((product) => [product.id, product])
  );

  return uniqueIds.map((id) => productsById.get(id)).filter(Boolean);
}
